/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';

/**
 * internal dependencies
 */
import useAuth from '../../../hooks/auth-hooks';
import { ICards } from '../../../models/cards.interface';
import { formatDate } from '../../../helpers/helpers';

/**
 * 
 * @param table 
 * @param data 
 */
export const displayCardListTable = (table: HTMLElement, data: ICards[]): TabulatorFull => {
  const { accessLevel, accessType } = useAuth()!;

  /**
   * Formats the card number to display as inactive or active.
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted card number
   */
  const format_inactiveCards = (cell: CellComponent, formatterParams: any) => {
    const { cardNumber, status } = cell.getData() as ICards;
    const cardStatus = status === 0 ? 'inactive' : 'active';
    const cardClass = `aafp_card-${cardStatus}`;

    return `<span class="${cardClass}">${cardNumber}</span>`;
  };

  /**
   * Formats the edit button to display as either an edit or view button.
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted edit button
   */
  const edit_card = (cell: CellComponent, formatterParams: any) => {
    const { cardNumber, customerId, cardGroupId } = cell.getData() as ICards;
    const userPrev = accessType === 'E' ? 'Edit' : 'View';
    const accessLevelEdit = accessLevel === 'A' && userPrev === 'Edit';

    //const cardAction = accessLevelEdit ? 'Edit' : 'View';
    const groupAction = userPrev.toLowerCase();

    const cardLink = `/cards/${userPrev === 'Edit' ? 'card-edit' : 'card-view'}/?cardAction=${userPrev}&card=${cardNumber}&custID=${customerId}`;
    const groupLink = `/card-group/${groupAction}-card-group?groupAction=${groupAction}&groupID=${cardGroupId}`;

    const cardIcon = userPrev === 'Edit' ? 'fas fa-pen' : 'far fa-eye';
    const groupIcon = 'fas fa-layer-group';

    const cardTitle = accessLevelEdit ? 'Edit Card' : 'View Card';
    const groupTitle = userPrev === 'Edit' ? 'Edit Card Group' : 'View Card Group';

    const cardButton = `<a class="aafp_btn-table btn_theme-primary"
                          href="${cardLink}"
                          data-toggle="tooltip"
                          data-placement="bottom" 
                          title="${cardTitle}"
                        >
                          <i class="${cardIcon}"></i>
                        </a>`;

    const groupButton = `<a class="aafp_btn-table btn_theme-outline"
                          href="${groupLink}"
                          data-toggle="tooltip"
                          data-placement="bottom" 
                          title="${groupTitle}"
                        >
                          <i class="${groupIcon}"></i>
                        </a>`;

    if (userPrev === 'Edit') {
      return `${cardButton} ${groupButton}`;
    } else {
      return cardButton;
    }  
  }

  /**
   * Formats the date into a more human readable format.
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted date
   */
  const table_format_date = (cell: CellComponent, formatterParams: any) => {
    const { LastTransaction } = cell.getData() as ICards;
    return formatDate(LastTransaction);
  }

  /**
   * Formats the status to display as a pill based upon the status.
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted status
   */
  const formatStatus = (cell: CellComponent, formatterParams: any) => {  
    const { status } = cell.getData() as ICards;
    const cardStatus = status === 0 ? 'Inactive' : 'Active';
    return `<span class="card-${cardStatus.toLowerCase()}">${cardStatus}</span>`;
  }

  const formatStatusDownload = (value: any, data: any, type: any, params: any) => {
    const { status } = data as ICards;
    return status === 0 ? 'Inactive' : 'Active';
  }

  const options: Options = {
    index: 'cardNumber',
    data: data,
    placeholder: "No Data Available",
    placeholderHeaderFilter: "No Matching Data",
    pagination: true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 75],
    paginationCounter: "rows",
    selectableRows: false,
    columns: [
      { title: 'Card Number', field: 'cardNumber', formatter: format_inactiveCards, resizable: false, vertAlign: 'middle' },
      { title: 'Card Name', field: 'cardName', resizable: false, vertAlign: 'middle' },
      { title: "Card Group Name", field: "cardGroupName", resizable: false, vertAlign: 'middle' },
      { title: "Customer Name", field: "customerName", resizable: false, vertAlign: 'middle', download: false },
      { title: "Status", field: 'status', formatter: formatStatus, accessorDownload: formatStatusDownload, resizable: false, vertAlign: 'middle', visible: false, download: true },
      { title: '', field: "EditButton", formatter: edit_card, width: 80, hozAlign: 'right', headerSort: false, resizable: false }
    ]
  };

  const cardListTableGrid = new TabulatorFull(table, options);
  return cardListTableGrid;
};